import Country from 'country-state-city/lib/country';
import State from 'country-state-city/lib/state';
import { getStateName } from 'localized-countries-states';
import { getDefaultPhoneCountryIso } from './regionalDefaults';
import { getUiLocaleTag } from './regionalFormat';

export const DEFAULT_COUNTRY_ISO = 'IN';

function preferredCountryIso() {
    return getDefaultPhoneCountryIso() || DEFAULT_COUNTRY_ISO;
}

/** @type {Map<string, import('country-state-city').ICity[]>} */
const cityCache = new Map();

/** @type {Map<string, Record<string, { de?: string, fr?: string, nl?: string }>>} */
const cityI18nCache = new Map();

/** @type {Map<string, Promise<Record<string, { de?: string, fr?: string, nl?: string }>>>} */
const cityI18nPending = new Map();

/** @type {Promise<{ default: typeof import('country-state-city/lib/city').default }> | null} */
let cityModulePromise = null;

let cachedCountryOptions = null;
let cachedCountryLocale = null;

function loadCityModule() {
    if (!cityModulePromise) {
        cityModulePromise = import('country-state-city/lib/city');
    }
    return cityModulePromise;
}

function uiLang() {
    return String(getUiLocaleTag() || 'en').slice(0, 2).toLowerCase();
}

function normCityKey(s) {
    return String(s || '')
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

function localizedCountryLabel(iso, englishName, locale) {
    try {
        if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
            const label = new Intl.DisplayNames([locale], { type: 'region' }).of(iso);
            if (label) return label;
        }
    } catch {
        /* fall through */
    }
    return englishName;
}

function localizedStateLabel(countryIso, stateIso, englishName) {
    try {
        const label = getStateName(countryIso, stateIso, uiLang());
        if (label && String(label).trim()) return label;
    } catch {
        /* fall through */
    }
    return englishName;
}

async function loadCityI18nMap(countryIso) {
    const cc = String(countryIso || '').toUpperCase();
    if (!cc) return {};
    if (cityI18nCache.has(cc)) return cityI18nCache.get(cc);
    if (cityI18nPending.has(cc)) return cityI18nPending.get(cc);

    // Use absolute site-root path (avoid import.meta — Jest cannot parse it).
    const pending = fetch(`/geo/city-i18n/${cc}.json`)
        .then(async (res) => {
            if (!res.ok) return {};
            return res.json();
        })
        .catch(() => ({}))
        .then((map) => {
            cityI18nCache.set(cc, map || {});
            cityI18nPending.delete(cc);
            return cityI18nCache.get(cc);
        });

    cityI18nPending.set(cc, pending);
    return pending;
}

function cityLabelFromMap(map, englishName, lang) {
    if (!englishName) return englishName;
    if (!lang || lang === 'en' || !map) return englishName;
    const key = normCityKey(englishName);
    let entry = map[key];
    // Common CSC / legacy variants
    if (!entry && key.endsWith(' city')) entry = map[key.slice(0, -5)];
    if (!entry && key.startsWith('city of ')) entry = map[key.slice(8)];
    const localized = entry?.[lang];
    return (localized && String(localized).trim()) || englishName;
}

function withCityLabels(cities, map, lang) {
    return (cities || []).map((city) => ({
        ...city,
        label: cityLabelFromMap(map, city.name, lang),
    }));
}

/**
 * Country options for address dropdowns.
 * `name` stays English (DB storage / matching); `label` follows UI language.
 */
export function getCountryOptions() {
    const locale = getUiLocaleTag();
    if (cachedCountryOptions && cachedCountryLocale === locale) {
        return cachedCountryOptions;
    }

    const all = Country.getAllCountries().map((c) => ({
        ...c,
        label: localizedCountryLabel(c.isoCode, c.name, locale),
    }));
    const sorted = [...all].sort((a, b) => a.label.localeCompare(b.label, locale));
    const preferredIso = preferredCountryIso();
    const preferredIndex = sorted.findIndex((c) => c.isoCode === preferredIso);
    if (preferredIndex > 0) {
        const [preferred] = sorted.splice(preferredIndex, 1);
        sorted.unshift(preferred);
    }
    cachedCountryOptions = sorted;
    cachedCountryLocale = locale;
    return sorted;
}

export function findCountryByName(name) {
    if (!name || !String(name).trim()) {
        return null;
    }
    const term = String(name).trim();
    return Country.getAllCountries().find(
        (c) => c.name === term || c.isoCode.toUpperCase() === term.toUpperCase()
    ) || null;
}

/**
 * State options — `name` English for DB; `label` follows Header UI language.
 */
export function getStateOptions(countryName) {
    const country = findCountryByName(countryName);
    if (!country) {
        return [];
    }
    const locale = getUiLocaleTag();
    return State.getStatesOfCountry(country.isoCode)
        .map((s) => ({
            ...s,
            label: localizedStateLabel(country.isoCode, s.isoCode, s.name),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, locale));
}

export function findStateByName(countryName, stateName) {
    if (!stateName || !String(stateName).trim()) {
        return null;
    }
    const term = String(stateName).trim();
    return getStateOptions(countryName).find(
        (s) => s.name === term || s.label === term || s.isoCode === term
    ) || null;
}

/**
 * Loads cities for a state. City dataset (~8MB) is fetched lazily on first use.
 * `name` stays English for DB storage; `label` follows Header UI language when a
 * GeoNames alternate-name overlay exists under /geo/city-i18n/{CC}.json.
 */
export async function loadCitiesForState(countryName, stateName) {
    const country = findCountryByName(countryName);
    const state = findStateByName(countryName, stateName);
    if (!country || !state) {
        return [];
    }

    const lang = uiLang();
    const cacheKey = `${country.isoCode}-${state.isoCode}:${lang}`;
    if (cityCache.has(cacheKey)) {
        return cityCache.get(cacheKey);
    }

    const rawKey = `${country.isoCode}-${state.isoCode}:raw`;
    let raw = cityCache.get(rawKey);
    if (!raw) {
        const { default: City } = await loadCityModule();
        raw = City.getCitiesOfState(country.isoCode, state.isoCode) || [];
        cityCache.set(rawKey, raw);
    }

    const map = await loadCityI18nMap(country.isoCode);
    const cities = withCityLabels(raw, map, lang);
    cityCache.set(cacheKey, cities);
    return cities;
}

/**
 * Localized display label for a stored English city name (async; uses city-i18n overlay).
 */
export async function getCityLabel(countryName, cityName) {
    const country = findCountryByName(countryName);
    const english = (cityName || '').trim();
    if (!country || !english) return english;
    const lang = uiLang();
    if (lang === 'en') return english;
    const map = await loadCityI18nMap(country.isoCode);
    return cityLabelFromMap(map, english, lang);
}

/**
 * Sync city label when the country i18n map is already cached; otherwise English.
 * Useful for address lines when async load is not practical.
 */
export function getCityLabelSync(countryName, cityName) {
    const country = findCountryByName(countryName);
    const english = (cityName || '').trim();
    if (!country || !english) return english;
    const lang = uiLang();
    if (lang === 'en') return english;
    const map = cityI18nCache.get(country.isoCode) || {};
    return cityLabelFromMap(map, english, lang);
}

/** Prefetch city-i18n map for a country so sync label helpers can localize. */
export function prefetchCityI18n(countryName) {
    const country = findCountryByName(countryName);
    if (!country) return Promise.resolve({});
    return loadCityI18nMap(country.isoCode);
}

export function filterCities(cities, query, limit = 30) {
    if (!Array.isArray(cities) || cities.length === 0) {
        return [];
    }
    const term = (query || '').trim().toLowerCase();
    if (!term) {
        return cities.slice(0, limit);
    }
    return cities
        .filter((city) => {
            const name = String(city.name || '').toLowerCase();
            const label = String(city.label || city.name || '').toLowerCase();
            return name.includes(term) || label.includes(term);
        })
        .slice(0, limit);
}

export function ensureNamedOption(options, currentValue, labelKey = 'name') {
    const value = (currentValue || '').trim();
    if (!value) {
        return options;
    }
    if (options.some((opt) => opt[labelKey] === value || opt.name === value || opt.label === value)) {
        return options;
    }
    return [{ [labelKey]: value, name: value, label: value, isoCode: value, legacy: true }, ...options];
}

export function ensureStringOption(options, currentValue) {
    const value = (currentValue || '').trim();
    if (!value) {
        return options;
    }
    if (options.includes(value)) {
        return options;
    }
    return [value, ...options];
}
